import { css } from '@linaria/core'
import { TabBaseSC } from 'atoms/Tab'
import { ContainerDescriptionStyled } from 'blocks/Container/Description/ContainerDescription.styled'
import { GridWrapper } from 'blocks/Grid'
import { ListStyled } from 'blocks/List'
import { BANNER_RESERVED_HEIGHT_PROPERTY } from 'blocks/PosterContent'
import { TimeLineSC } from 'blocks/TimeLine'
import { FooterSC } from 'containers/Footer/Footer.styled'
import { variables } from 'theme/variables'
import {
  COLOR,
  FIXED_INDENTS,
  LAYOUT,
  MEDIA,
  SHADOW,
  TRANSITIONS,
} from 'vars/index'
import { SUBTITLE_FONT_FAMILIES, SUBTITLE_FONTS } from './subtitle-fonts'
import { FONT_MAIN } from './typography'

export const globals = css`
  :global() {
    ${FONT_MAIN}
    ${SUBTITLE_FONTS}
    ${SUBTITLE_FONT_FAMILIES}
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
      outline: none;
      box-shadow: none;
    }

    html {
      min-height: 100vh;
      min-height: -webkit-fill-available;
      height: 100%;
      overflow-y: scroll;
      scrollbar-gutter: stable;
      background: var(${COLOR.BACKGROUND_DARK});
    }

    body {
      --scrollbar-width: 11px;
      margin: 0;
      height: 100%;
      min-height: -webkit-fill-available;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      background: var(${COLOR.BACKGROUND_DARK});
    }

    #root {
      height: 100%;
      display: flex;
      flex-direction: column;
    }

    body::-webkit-scrollbar {
      width: var(--scrollbar-width);
    }

    body::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0.15);
    }

    body::-webkit-scrollbar-thumb {
      border-right: 16px;
      background: var(${COLOR.SURFACE_OPACITY_DARK_80});
    }

    #root > * {
      height: 100%;

      color: var(${COLOR.TEXT_ICON_PRIMARY});
    }

    a {
      text-decoration: none;
      color: inherit;
    }

    //============== ScaleUp_120=============
    .scale-up-entering {
      transform: scale(1);
    }

    .scale-up-entered {
      transform: scale(1.2);
      transition: transform 0.2s ease;
    }

    .scale-up-exiting {
      transform: scale(1.2);
      font-size: 1em;
    }

    .scale-up-exited {
      transition: transform var(${TRANSITIONS.LINEAR_400});
      transform: scale(1);
    }

    //============== ScaleUp_110 =============
    .scale-up-110-entering {
      transform: scale(1);
    }

    .scale-up-110-entered {
      transform: scale(1.1);
      transition: transform 0.2s ease;
    }

    .scale-up-110-exiting {
      transform: scale(1.1);
      font-size: 1em;
    }

    .scale-up-110-exited {
      transition: transform var(${TRANSITIONS.LINEAR_400});
      transform: scale(1);
    }

    // =============== ShadowNormal ===============
    .shadow-normal-entering {
      box-shadow: none;
    }

    .shadow-normal-entered {
      box-shadow: var(${SHADOW.NORMAL});
      transition: box-shadow var(${TRANSITIONS.LINEAR_400});
    }

    .shadow-normal-exiting {
      box-shadow: var(${SHADOW.NORMAL});
    }

    .shadow-normal-exited {
      transition: box-shadow var(${TRANSITIONS.LINEAR_400});
      box-shadow: none;
    }

    // =============== ShadowHuge ===============
    .shadow-huge-entering {
      box-shadow: none;
    }

    .shadow-huge-entered {
      box-shadow: var(${SHADOW.HUGE});
      transition: box-shadow var(${TRANSITIONS.LINEAR_400});
    }

    .shadow-huge-exiting {
      box-shadow: var(${SHADOW.HUGE});
    }

    .shadow-huge-exited {
      transition: box-shadow var(${TRANSITIONS.LINEAR_400});

      box-shadow: none;
    }

    //=============== FadeIn ===============
    .fade-in {
      // Property for safari. This fixes the resizing of the elements (fractional pixels)
      will-change: opacity;
    }

    .fade-in-entering {
      opacity: 0;
    }

    .fade-in-entered {
      opacity: 1;
      transition: opacity var(${TRANSITIONS.LINEAR_400});
    }

    .fade-in-exiting {
      opacity: 1;
    }

    .fade-in-exited {
      opacity: 0;
      transition: opacity var(${TRANSITIONS.LINEAR_400});
    }

    //=============== FadeInInputs ===============
    .fade-in-inputs.fade-in-inputs-entering {
      opacity: 0;
    }

    .fade-in-inputs.fade-in-inputs-entered {
      animation: inAnimation 0.75s;
      @keyframes inAnimation {
        0% {
          opacity: 0;
          max-height: 150px;
        }
        100% {
          opacity: 1;
          max-height: 100%;
        }
      }
    }

    .fade-in-inputs.fade-in-inputs-exiting {
      opacity: 1;
    }

    .fade-in-inputs.fade-in-inputs-exited {
      animation: outAnimation 0.75s;
      @keyframes outAnimation {
        0% {
          opacity: 1;
          max-height: 100%;
        }
        100% {
          opacity: 0;
          max-height: 150px;
        }
      }
    }

    //=============== SelectionTabs ===============
    ${TabBaseSC}.selection-tabs.selection-tabs-entering {
      background-color: var(${COLOR.TRANSPARENT});
    }

    ${TabBaseSC}.selection-tabs.selection-tabs-entered {
      background-color: var(${COLOR.ACCENT_SECONDARY});
      transition: background-color var(${TRANSITIONS.LINEAR_300});
    }

    ${TabBaseSC}.selection-tabs.selection-tabs-exiting {
      background-color: var(${COLOR.ACCENT_SECONDARY});
    }

    ${TabBaseSC}.selection-tabs.selection-tabs-exited {
      background-color: var(${COLOR.TRANSPARENT});
      transition: background-color var(${TRANSITIONS.LINEAR_300});
    }

    //=============== SlideInLeft ===============
    .slide-in-left-entering {
      transform: translateX(100%);
    }

    .slide-in-left-entered {
      transform: translateX(0%);
      transition: transform var(${TRANSITIONS.LINEAR_400});
    }

    .slide-in-left-exiting {
      transform: translateX(0%);
    }

    .slide-in-left-exited {
      transform: translateX(100%);
      transition: transform var(${TRANSITIONS.LINEAR_400});
    }

    //=============== SlideInLeft-300 ===============
    .slide-in-left-300-entering {
      transform: translateX(100%);
    }

    .slide-in-left-300-entered {
      transform: translateX(0%);
      transition: transform var(${TRANSITIONS.LINEAR_300});
    }

    .slide-in-left-300-exiting {
      transform: translateX(0%);
    }

    .slide-in-left-300-exited {
      transform: translateX(100%);
      transition: transform var(${TRANSITIONS.LINEAR_300});
    }

    //=============== SlideInRight ===============
    .slide-in-right-entering {
      transform: translateX(-100%);
    }

    .slide-in-right-entered {
      transform: translateX(0%);
      transition: transform var(${TRANSITIONS.LINEAR_400});
    }

    .slide-in-right-exiting {
      transform: translateX(0%);
    }

    .slide-in-right-exited {
      transform: translateX(-100%);
      transition: transform var(${TRANSITIONS.LINEAR_400});
    }

    .page-wrapper {
      ${MEDIA.MAX_1239_OR_LANDSCAPE} {
        min-height: 100dvh;
      }

      &:not(.content-details-page) > :not(${GridWrapper}):last-child {
        padding-bottom: var(${FIXED_INDENTS.INDENT_70});
      }

      /*
       * FP-4321: without a recommendations GridWrapper the leftover viewport
       * sat between the details block and the footer. Keep this page
       * content-sized and drop the sticky-footer auto margin.
       *
       * FP-4408: PosterContent is the only part of a details page that stands
       * in the flow — the poster and trailer shells are out of flow, and
       * RenderSkin paints empty placeholders until a skin resolves — so before
       * it mounts the wrapper collapses to its navbar padding and the footer
       * lands under the header. Its published height is the page's content
       * size once it is there, and a full viewport holds the footer off the
       * screen until then.
       */
      &.content-details-page {
        flex: 0 0 auto;
        min-height: var(${BANNER_RESERVED_HEIGHT_PROPERTY}, 100dvh);

        & ~ ${FooterSC} {
          margin-top: 0;
        }
      }

      & > ${ContainerDescriptionStyled}:first-child {
        height: auto;
        flex: 0 0 auto;
      }

      &.epg-page,
      &.live-events-page {
        > ${ListStyled}:first-child {
          position: sticky;
          top: var(${LAYOUT.NavbarSize});
          z-index: 3;
        }
      }

      &.epg-page {
        > :last-child {
          padding-bottom: 0;
        }

        ${TimeLineSC} {
          position: sticky;
          top: calc(
            var(${LAYOUT.NavbarSize}) + 94px
          ); /* 94px is CategoryTabs height */
          z-index: 3;
        }
      }
    }

    .flex-column {
      display: flex;
      flex-direction: column;
    }

    .full-page-height {
      min-height: 100%;
    }

    .${variables} {
      min-height: 100%;
      display: flex;
      flex-direction: column;
      position: relative;
      width: 100%;
      height: 100%;

      ${MEDIA.TOUCH} {
        overflow: auto;
      }
    }
  }
`
